import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Users, AlertTriangle } from "lucide-react";
import { adminApi } from "@/lib/admin";

/**
 * UserAnalyticsSection — admin-only user-behavior roll-up:
 *   • Total registered users (non-admin)
 *   • Active today / 7d / 30d
 *   • Users at the 10/day cap AND near it
 *   • Top users by queries today AND by tokens this UTC month
 *   • Users approaching the 1M/month token cap
 *
 * Auto-refresh every 60s. Token counts are ADMIN-ONLY and are never
 * exposed to the end user in any product surface — this panel is the
 * single place operators can see them.
 */

function fmtTokens(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function Bar({ pct, warn }) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  const color = warn ? "bg-orange-400" : "bg-[var(--text)]/80";
  return (
    <div className="w-24 h-1.5 bg-[var(--text)]/10 relative overflow-hidden">
      <div
        className={`h-full ${color}`}
        style={{ width: `${p}%` }}
      />
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-[var(--border)] p-4">
      <p
        data-testid={testid}
        className="font-mono-ui text-3xl font-medium text-[var(--text)]"
      >
        {value}
      </p>
      <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] mt-2">
        {label}
      </p>
    </div>
  );
}

export default function UserAnalyticsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await adminApi.userAnalytics();
      setData(r);
    } catch (err) {
      setError(err.message || "Couldn't load user analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  if (loading && !data) {
    return (
      <p
        data-testid="user-analytics-loading"
        className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] mb-3"
      >
        <Loader2 size={12} className="inline animate-spin mr-2" />
        Loading user analytics…
      </p>
    );
  }
  if (error) {
    return (
      <p
        data-testid="user-analytics-error"
        className="font-sans text-sm text-red-400 border-l-2 border-red-400 pl-3 mb-3"
      >
        {error}
      </p>
    );
  }
  if (!data) return null;

  const caps = data.caps || {};

  return (
    <div className="space-y-8" data-testid="user-analytics-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="font-sans text-sm text-[var(--text-dim)] max-w-2xl leading-relaxed">
          Non-admin users only. Defaults enforced right now:{" "}
          <span className="text-[var(--text)] font-mono-ui">
            {caps.user_daily_queries} queries/day
          </span>{" "}
          and{" "}
          <span className="text-[var(--text)] font-mono-ui">
            {fmtTokens(caps.user_monthly_tokens)} tokens/month
          </span>
          .
        </p>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="user-analytics-refresh"
          className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] hover:text-[var(--text)] transition-colors inline-flex items-center gap-2"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total users" value={data.total_users} testid="ua-total-users" />
        <Stat label="Active today" value={data.active_today} testid="ua-active-today" />
        <Stat label="Active 7d" value={data.active_7d} testid="ua-active-7d" />
        <Stat label="Active 30d" value={data.active_30d} testid="ua-active-30d" />
      </div>

      {/* Users at the daily cap right now */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={13} className="text-orange-400" />
          <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
            At today&apos;s cap &nbsp;·&nbsp;{" "}
            {data.at_daily_cap?.length || 0}
          </p>
        </div>
        {(data.at_daily_cap || []).length === 0 ? (
          <p className="font-sans text-sm text-[var(--text-dim)] italic">
            No users have hit the daily cap yet today.
          </p>
        ) : (
          <div className="border border-[var(--border)]">
            <table className="w-full font-mono-ui text-[11px]">
              <thead className="bg-[var(--surface-2,rgba(255,255,255,0.03))]">
                <tr className="text-left">
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">Email</th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">Queries today</th>
                </tr>
              </thead>
              <tbody>
                {data.at_daily_cap.map((r, i) => (
                  <tr key={r.user_id + i} className="border-t border-[var(--border)]">
                    <td className="px-3 py-2 text-[var(--text)] break-all">{r.email}</td>
                    <td className="px-3 py-2 text-orange-400">
                      {r.queries_today} / {r.daily_message_cap || caps.user_daily_queries}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top daily queries */}
      <div className="space-y-3">
        <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
          Top 15 by queries today
        </p>
        {(data.top_daily || []).length === 0 ? (
          <p className="font-sans text-sm text-[var(--text-dim)] italic">
            No chat traffic today yet.
          </p>
        ) : (
          <div className="border border-[var(--border)]">
            <table className="w-full font-mono-ui text-[11px]" data-testid="ua-top-daily-table">
              <thead className="bg-[var(--surface-2,rgba(255,255,255,0.03))]">
                <tr className="text-left">
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">#</th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">Email</th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">Queries</th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">Utilization</th>
                </tr>
              </thead>
              <tbody>
                {data.top_daily.map((r, i) => {
                  const cap = r.daily_message_cap || caps.user_daily_queries;
                  const pct = Math.round((100 * (r.queries_today || 0)) / Math.max(1, cap));
                  return (
                    <tr key={r.user_id + i} className="border-t border-[var(--border)]">
                      <td className="px-3 py-2 text-[var(--text-dim)]">{i + 1}</td>
                      <td className="px-3 py-2 text-[var(--text)] break-all">{r.email}</td>
                      <td className="px-3 py-2 text-[var(--text)]">
                        {r.queries_today}/{cap}
                      </td>
                      <td className="px-3 py-2">
                        <Bar pct={pct} warn={pct >= 80} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top monthly token consumers */}
      <div className="space-y-3">
        <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
          Top 15 by tokens this month
          <span className="ml-2 text-[10px] normal-case tracking-normal text-[var(--text-dim)]/70">
            (admin-only view — never shown to users)
          </span>
        </p>
        {(data.top_monthly_tokens || []).length === 0 ? (
          <p className="font-sans text-sm text-[var(--text-dim)] italic">
            No token usage recorded this month.
          </p>
        ) : (
          <div className="border border-[var(--border)]">
            <table className="w-full font-mono-ui text-[11px]" data-testid="ua-top-monthly-table">
              <thead className="bg-[var(--surface-2,rgba(255,255,255,0.03))]">
                <tr className="text-left">
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">#</th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">Email</th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">Tokens</th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">% of cap</th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">Utilization</th>
                </tr>
              </thead>
              <tbody>
                {data.top_monthly_tokens.map((r, i) => (
                  <tr key={r.user_id + i} className="border-t border-[var(--border)]">
                    <td className="px-3 py-2 text-[var(--text-dim)]">{i + 1}</td>
                    <td className="px-3 py-2 text-[var(--text)] break-all">{r.email}</td>
                    <td className="px-3 py-2 text-[var(--text)]">{fmtTokens(r.tokens_month)}</td>
                    <td className={`px-3 py-2 ${r.pct_of_cap >= 80 ? "text-orange-400" : "text-[var(--text)]"}`}>
                      {r.pct_of_cap}%
                    </td>
                    <td className="px-3 py-2">
                      <Bar pct={r.pct_of_cap} warn={r.pct_of_cap >= 80} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Near monthly cap */}
      {(data.near_monthly_cap || []).length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={13} className="text-orange-400" />
            <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
              ≥ 80% of monthly token cap &nbsp;·&nbsp; {data.near_monthly_cap.length}
            </p>
          </div>
          <div className="border border-[var(--border)]">
            <table className="w-full font-mono-ui text-[11px]">
              <thead className="bg-[var(--surface-2,rgba(255,255,255,0.03))]">
                <tr className="text-left">
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">Email</th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">Tokens</th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">% of cap</th>
                </tr>
              </thead>
              <tbody>
                {data.near_monthly_cap.map((r, i) => (
                  <tr key={r.user_id + i} className="border-t border-[var(--border)]">
                    <td className="px-3 py-2 text-[var(--text)] break-all">{r.email}</td>
                    <td className="px-3 py-2 text-[var(--text)]">{fmtTokens(r.tokens_month)}</td>
                    <td className="px-3 py-2 text-orange-400">{r.pct_of_cap}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-2">
        <Users size={12} className="text-[var(--text-dim)]" />
        <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
          Generated at {new Date(data.generated_at).toLocaleString()} · Auto-refresh 60s
        </p>
      </div>
    </div>
  );
}
